import React, {useState, useRef, useEffect} from 'react'
import {Box, Input, List, ListItem, VStack, useOutsideClick, InputGroup, Tag, TagLabel, TagCloseButton, Icon, FormControl, FormLabel, FormErrorMessage} from '@chakra-ui/react'
import {SmallCloseIcon, InfoIcon} from '@chakra-ui/icons'

interface AutocompleteOption {
  value: string
  label: string
}

interface AutocompleteProps {
  options: AutocompleteOption[]
  name?: string
  value?: string
  onChange?: (value: string) => void
  label?: string
  placeholder?: string
  size?: 'sm' | 'md' | 'lg'
  isDisabled?: boolean
  error?: string
  touched?: boolean
}

export const Autocomplete: React.FC<AutocompleteProps> = ({
  options,
  name,
  value: externalValue,
  onChange: externalOnChange,
  label,
  placeholder = 'Type to search...',
  size = 'md',
  isDisabled,
  error,
  touched
}) => {
  const [inputValue, setInputValue] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1)
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteOption[]>(options)
  const [selectedOption, setSelectedOption] = useState<AutocompleteOption | null>(null)

  const containerRef = useRef<HTMLDivElement>(null)

  useOutsideClick({
    ref: containerRef,
    handler: () => setIsOpen(false)
  })

  useEffect(() => {
    if (externalValue) {
      const selected = options.find((option) => option.value === externalValue)
      if (selected) {
        setSelectedOption(selected)
        setInputValue(selected.label)
      }
    } else {
      setSelectedOption(null)
      setInputValue('')
    }
  }, [externalValue, options])

  useEffect(() => {
    if (inputValue === '') {
      setFilteredOptions(options)
    } else {
      const filtered = options.filter((option) => option.label.toLowerCase().includes(inputValue.toLowerCase()))
      setFilteredOptions(filtered)
    }
  }, [inputValue, options])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)
    setIsOpen(true)

    if (value === '' || (selectedOption && !selectedOption.label.toLowerCase().includes(value.toLowerCase()))) {
      setSelectedOption(null)
      externalOnChange?.('')
    }
  }

  const handleOptionSelect = (option: AutocompleteOption) => {
    setSelectedOption(option)
    setInputValue(option.label)
    setIsOpen(false)
    externalOnChange?.(option.value)
    console.debug(`Selected option`, option.value)
  }

  const handleClear = () => {
    setSelectedOption(null)
    setInputValue('')
    externalOnChange?.('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightedIndex >= 0 && highlightedIndex < filteredOptions.length) {
        handleOptionSelect(filteredOptions[highlightedIndex])
      }
    } else if (e.key === 'Escape') {
      setIsOpen(false)
    }
  }

  const highlightMatch = (label: string, query: string) => {
    if (!query) {
      return label
    }
    const regex = new RegExp(`(${query})`, 'gi')
    const parts = label.split(regex)
    return (
      <>
        {parts.map((part, i) =>
          part.toLowerCase() === query.toLowerCase() ? (
            <Box as="span" key={i} fontWeight="bold" color="blue.500">
              {part}
            </Box>
          ) : (
            <Box as="span" key={i}>
              {part}
            </Box>
          )
        )}
      </>
    )
  }

  const content = (
    <Box position="relative" ref={containerRef} width="100%">
      <InputGroup>
        {selectedOption ? (
          <Box
            border="1px solid"
            borderColor={touched && error ? 'red.500' : 'gray.200'}
            borderRadius="md"
            p={2}
            width="100%"
            height={size === 'sm' ? '2rem' : size === 'md' ? '2.5rem' : '3rem'}
            display="flex"
            alignItems="center">
            <Tag size={size} variant="subtle" colorScheme="blue" borderRadius="full" overflow="hidden">
              <TagLabel>{selectedOption.label}</TagLabel>
              <TagCloseButton onClick={handleClear} fontSize={size === 'sm' ? 'sm' : size === 'md' ? 'md' : 'lg'} />
            </Tag>
          </Box>
        ) : (
          <Input
            name={name}
            value={inputValue}
            onChange={handleInputChange}
            onFocus={() => setIsOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-autocomplete="list"
            aria-controls="autocomplete-list"
            aria-expanded={isOpen}
            size={size}
            isDisabled={isDisabled}
            borderColor={touched && error ? 'red.500' : undefined}
          />
        )}
      </InputGroup>
      {isOpen && (
        <Box position="absolute" width="100%" bg="white" borderWidth="1px" borderRadius="md" boxShadow="sm" mt={1} maxHeight="200px" overflowY="auto" zIndex={1000}>
          {filteredOptions.length > 0 ? (
            <List id="autocomplete-list" spacing={0}>
              {filteredOptions.map((option, index) => (
                <ListItem
                  key={option.value}
                  padding={2}
                  bg={index === highlightedIndex ? 'gray.100' : 'white'}
                  fontSize={size === 'sm' ? 'sm' : size === 'md' ? 'md' : 'lg'}
                  cursor="pointer"
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onMouseLeave={() => setHighlightedIndex(-1)}
                  onClick={() => handleOptionSelect(option)}
                  role="option"
                  aria-selected={index === highlightedIndex}>
                  <VStack align="start" spacing={0}>
                    <Box>{highlightMatch(option.label, inputValue)}</Box>
                  </VStack>
                </ListItem>
              ))}
            </List>
          ) : (
            <Box padding={2} textAlign="center" color="gray.500" fontSize={size === 'sm' ? 'sm' : size === 'md' ? 'md' : 'lg'}>
              <Icon as={InfoIcon} mr={2} />
              No options found.
            </Box>
          )}
        </Box>
      )}
    </Box>
  )

  return (
    <FormControl isInvalid={!!(touched && error)} isDisabled={isDisabled}>
      {label && <FormLabel>{label}</FormLabel>}
      {content}
      {touched && error && <FormErrorMessage>{error}</FormErrorMessage>}
    </FormControl>
  )
}
